import { useEffect, useMemo, useState } from "react";
import { History, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabaseClient";
import { formatDoctorName } from "@/lib/doctorName";
import {
  isDentureMvProcedure,
  isGenericMvProcedure,
  isPartialDentureProcedure,
} from "@/lib/mvImport";

const ARCH_TEETH = {
  upper: {
    left: ["18", "17", "16", "15", "14", "13", "12", "11"],
    right: ["21", "22", "23", "24", "25", "26", "27", "28"],
  },
  lower: {
    left: ["48", "47", "46", "45", "44", "43", "42", "41"],
    right: ["31", "32", "33", "34", "35", "36", "37", "38"],
  },
};

function emptyForm() {
  return {
    procedure_id: "",
    doctor_id: "",
    agreed_pesos: "",
    second_agreed_pesos: "",
    paid_prior_pesos: "0",
    tooth_number: "",
    surface: "",
    arch: "",
    teeth: [],
    denture_count: 1,
    second_teeth: [],
    notes: "",
  };
}

function newIdempotencyKey() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `mv-import-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export default function MvCaseImportDialog({
  open,
  patientId,
  sourceEntryPoint = "patient_record",
  appointmentId = null,
  existingItemId = null,
  procedures: providedProcedures = null,
  doctors: providedDoctors = null,
  initialProcedure = null,
  initialDoctorId = "",
  onClose,
  onImported,
}) {
  const [form, setForm] = useState(emptyForm);
  const [procedures, setProcedures] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);

  useEffect(() => {
    if (!open) return;
    const next = emptyForm();
    next.procedure_id = initialProcedure?.id || "";
    next.doctor_id = initialDoctorId || "";
    next.agreed_pesos = initialProcedure?.default_fee_centavos != null
      ? String(Number(initialProcedure.default_fee_centavos) / 100)
      : "";
    next.second_agreed_pesos = next.agreed_pesos;
    setForm(next);
    setIdempotencyKey(newIdempotencyKey());

    let cancelled = false;
    setLoading(true);
    Promise.all([
      Array.isArray(providedProcedures)
        ? Promise.resolve({ data: providedProcedures, error: null })
        : supabase.rpc("list_clinic_procedures"),
      Array.isArray(providedDoctors)
        ? Promise.resolve({ data: providedDoctors, error: null })
        : supabase.from("doctors").select("id, display_name, profile_id").eq("archived", false),
    ]).then(([procedureResult, doctorResult]) => {
      if (cancelled) return;
      setLoading(false);
      if (procedureResult.error || doctorResult.error) {
        toast.error(procedureResult.error?.message || doctorResult.error?.message || "Could not load import choices.");
        return;
      }
      setProcedures((procedureResult.data || []).filter(isGenericMvProcedure));
      setDoctors(doctorResult.data || []);
    });
    return () => { cancelled = true; };
  }, [open, providedProcedures, providedDoctors, initialProcedure, initialDoctorId]);

  const selectedProcedure = useMemo(
    () => procedures.find((procedure) => procedure.id === form.procedure_id)
      || (initialProcedure?.id === form.procedure_id ? initialProcedure : null),
    [procedures, form.procedure_id, initialProcedure],
  );
  const denture = isDentureMvProcedure(selectedProcedure);
  const partial = isPartialDentureProcedure(selectedProcedure);
  const collectsClinicalScope = sourceEntryPoint === "chairside";

  function update(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function chooseProcedure(procedureId) {
    const procedure = procedures.find((row) => row.id === procedureId);
    setForm((current) => ({
      ...current,
      procedure_id: procedureId,
      agreed_pesos: procedure?.default_fee_centavos != null
        ? String(Number(procedure.default_fee_centavos) / 100)
        : current.agreed_pesos,
      second_agreed_pesos: procedure?.default_fee_centavos != null
        ? String(Number(procedure.default_fee_centavos) / 100)
        : current.second_agreed_pesos,
      tooth_number: "",
      surface: "",
      arch: "",
      teeth: [],
      denture_count: 1,
      second_teeth: [],
    }));
  }

  function chooseArch(arch) {
    setForm((current) => ({
      ...current,
      arch,
      denture_count: arch === "both" ? 2 : 1,
      teeth: [],
      second_teeth: [],
    }));
  }

  function chooseDentureCount(count) {
    setForm((current) => ({
      ...current,
      denture_count: count,
      arch: count === 2 && collectsClinicalScope ? "both" : count === 1 ? "" : current.arch,
      teeth: [],
      second_teeth: [],
      second_agreed_pesos: current.second_agreed_pesos || current.agreed_pesos,
    }));
  }

  function toggleTooth(tooth, field = "teeth") {
    setForm((current) => ({
      ...current,
      [field]: current[field].includes(tooth)
        ? current[field].filter((value) => value !== tooth)
        : [...current[field], tooth],
    }));
  }

  async function submit(event) {
    event.preventDefault();
    if (submitting) return;
    if (!patientId) { toast.error("Choose a patient before importing a case."); return; }
    if (!selectedProcedure) { toast.error("Choose the ongoing procedure."); return; }
    if (!form.doctor_id) { toast.error("Choose the doctor taking over the case."); return; }
    const firstAgreed = Math.round(Number(form.agreed_pesos) * 100);
    const secondAgreed = denture && form.denture_count === 2
      ? Math.round(Number(form.second_agreed_pesos) * 100)
      : 0;
    const agreed = firstAgreed + secondAgreed;
    const paidPrior = Math.round(Number(form.paid_prior_pesos || 0) * 100);
    if (!Number.isSafeInteger(firstAgreed) || firstAgreed <= 0
      || (denture && form.denture_count === 2 && (!Number.isSafeInteger(secondAgreed) || secondAgreed <= 0))) {
      toast.error(denture ? "Enter the agreed price for each denture arch." : "Enter the agreed package amount.");
      return;
    }
    if (!Number.isSafeInteger(paidPrior) || paidPrior < 0 || paidPrior > agreed) {
      toast.error("Paid before Dental OS must be between zero and the package amount.");
      return;
    }
    if (collectsClinicalScope && !denture && selectedProcedure.tooth_specific && !form.tooth_number.trim()) {
      toast.error("Enter the tooth for this ongoing case.");
      return;
    }
    if (collectsClinicalScope && denture && !form.arch) { toast.error("Choose one arch or both arches."); return; }
    if (collectsClinicalScope && partial && form.teeth.length === 0) { toast.error("Select the teeth replaced by the first partial denture."); return; }
    if (collectsClinicalScope && partial && form.arch === "both" && form.second_teeth.length === 0) {
      toast.error("Select the teeth replaced by the lower partial denture.");
      return;
    }

    setSubmitting(true);
    const dentureItems = denture ? (
      form.denture_count === 2
        ? [
            {
              procedure_id: selectedProcedure.id,
              agreed_fee_centavos: firstAgreed,
              arch: collectsClinicalScope ? "upper" : null,
              teeth: collectsClinicalScope && partial ? form.teeth : [],
            },
            {
              procedure_id: selectedProcedure.id,
              agreed_fee_centavos: secondAgreed,
              arch: collectsClinicalScope ? "lower" : null,
              teeth: collectsClinicalScope && partial ? form.second_teeth : [],
            },
          ]
        : [{
            procedure_id: selectedProcedure.id,
            agreed_fee_centavos: firstAgreed,
            arch: collectsClinicalScope ? form.arch : null,
            teeth: collectsClinicalScope && partial ? form.teeth : [],
          }]
    ) : null;
    const { data, error } = await supabase.rpc(denture ? "import_ongoing_denture_case" : "import_ongoing_mv_case", {
      p_patient_id: patientId,
      p_input: {
        procedure_id: selectedProcedure.id,
        doctor_id: form.doctor_id,
        ...(!denture ? { agreed_fee_centavos: agreed } : {}),
        paid_prior_centavos: paidPrior,
        tooth_number: collectsClinicalScope && !denture && selectedProcedure.tooth_specific ? form.tooth_number.trim() || null : null,
        surface: collectsClinicalScope && !denture && selectedProcedure.surface_specific ? form.surface.trim() || null : null,
        arch: collectsClinicalScope && denture ? form.arch : null,
        teeth: collectsClinicalScope && partial ? form.teeth : [],
        notes: form.notes.trim() || null,
        source_entry_point: sourceEntryPoint,
        appointment_id: appointmentId || null,
        existing_item_id: existingItemId || null,
        idempotency_key: idempotencyKey,
        ...(denture ? { items: dentureItems } : {}),
      },
    });
    setSubmitting(false);
    if (error) { toast.error(error.message || "Could not import the ongoing case."); return; }
    toast.success("Ongoing case imported", {
      description: `Balance: ${formatPeso(data?.balance_centavos)}. The normal MV visit cycle is ready.`,
    });
    onImported?.({ ...data, doctor_id: form.doctor_id, procedure_id: selectedProcedure.id });
    onClose?.();
  }

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="mv-import-title"
      data-testid="mv-case-import-dialog"
      className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/45 p-4 backdrop-blur-sm"
      onMouseDown={(event) => { if (event.target === event.currentTarget && !submitting) onClose?.(); }}
    >
      <div className="flex max-h-[calc(100dvh-2rem)] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-4">
          <div>
            <div className="flex items-center gap-2">
              <History className="h-5 w-5 text-violet-600" />
              <h2 id="mv-import-title" className="text-lg font-bold text-slate-900">Import ongoing treatment</h2>
            </div>
            <p className="mt-1 text-sm text-slate-500">
              Enter the opening package and prior payment. {collectsClinicalScope ? "Confirm the clinical scope to continue the normal MV cycle." : "The dentist will confirm clinical scope in Today."}
            </p>
          </div>
          <button type="button" onClick={onClose} disabled={submitting} aria-label="Close" className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={submit} className="flex-1 overflow-y-auto px-6 py-5">
          {loading ? (
            <div className="flex min-h-52 items-center justify-center text-sm text-slate-500">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading clinic choices…
            </div>
          ) : (
            <div className="space-y-5">
              {initialProcedure || initialDoctorId ? (
                <div className="flex flex-wrap gap-2 rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-700">
                  {initialProcedure ? <span className="font-semibold">{initialProcedure.name}</span> : null}
                  {initialDoctorId ? <span>· {formatDoctorName(doctors.find((doctor) => (doctor.id || doctor.doctor_id) === initialDoctorId) || {}, "Assigned doctor")}</span> : null}
                </div>
              ) : null}

              <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {!initialProcedure ? (
                  <Field label="Ongoing procedure">
                    <select value={form.procedure_id} onChange={(event) => chooseProcedure(event.target.value)} data-testid="mv-import-procedure" className={inputClass}>
                      <option value="">Choose procedure</option>
                      {procedures.map((procedure) => <option key={procedure.id} value={procedure.id}>{procedure.name}</option>)}
                    </select>
                  </Field>
                ) : null}
                {!initialDoctorId ? (
                  <Field label="Doctor taking over">
                    <select value={form.doctor_id} onChange={(event) => update("doctor_id", event.target.value)} data-testid="mv-import-doctor" className={inputClass}>
                      <option value="">Choose doctor</option>
                      {doctors.map((doctor) => (
                        <option
                          key={doctor.id || doctor.user_id}
                          value={doctor.id || doctor.doctor_id || ""}
                        >
                          {formatDoctorName(doctor, doctor.display_name || doctor.full_name || "Doctor")}
                        </option>
                      ))}
                    </select>
                  </Field>
                ) : null}
                <Field label={denture ? (form.denture_count === 2 ? "Upper arch price" : "Agreed price — one arch") : "Agreed package"}>
                  <MoneyInput value={form.agreed_pesos} onChange={(value) => update("agreed_pesos", value)} testId="mv-import-package" />
                </Field>
                {denture && form.denture_count === 2 ? (
                  <Field label="Lower arch price">
                    <MoneyInput value={form.second_agreed_pesos} onChange={(value) => update("second_agreed_pesos", value)} testId="mv-import-second-package" />
                  </Field>
                ) : null}
                <Field label="Paid before Dental OS">
                  <MoneyInput value={form.paid_prior_pesos} onChange={(value) => update("paid_prior_pesos", value)} testId="mv-import-paid-prior" />
                  <p className="mt-1 text-[11px] text-slate-500">Opening balance only. This will not appear as today&apos;s collection or receipt.</p>
                </Field>
              </section>

              {denture && !collectsClinicalScope ? (
                <section className="space-y-3 rounded-xl border border-slate-200 p-4" data-testid="mv-import-denture-count">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">How many denture arches are included?</p>
                    <p className="text-xs text-slate-500">Reception records one or two. The dentist confirms which jaw and teeth in Today.</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {[[1, "One arch"], [2, "Two arches"]].map(([count, label]) => (
                      <button
                        key={count}
                        type="button"
                        onClick={() => chooseDentureCount(count)}
                        data-testid={`mv-import-denture-count-${count}`}
                        className={`rounded-lg border px-3 py-2 text-sm font-semibold ${form.denture_count === count ? "border-violet-600 bg-violet-600 text-white" : "border-slate-200 text-slate-700 hover:border-violet-300"}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </section>
              ) : null}

              {!collectsClinicalScope && selectedProcedure ? (
                <div className="rounded-xl border border-teal-100 bg-teal-50/60 px-4 py-3 text-xs text-teal-800" data-testid="mv-import-scope-deferred">
                  No tooth or denture selection is needed here. The dentist will confirm it in Today before recording the first visit.
                </div>
              ) : null}

              {collectsClinicalScope && !denture && selectedProcedure?.tooth_specific ? (
                <section className="grid grid-cols-1 gap-4 rounded-xl border border-slate-200 p-4 sm:grid-cols-2">
                  <Field label="Tooth number">
                    <input value={form.tooth_number} onChange={(event) => update("tooth_number", event.target.value)} placeholder="FDI, e.g. 46" data-testid="mv-import-tooth-number" className={inputClass} />
                  </Field>
                  {selectedProcedure.surface_specific ? (
                    <Field label="Surface">
                      <input value={form.surface} onChange={(event) => update("surface", event.target.value)} placeholder="Optional, e.g. MO" data-testid="mv-import-surface" className={inputClass} />
                    </Field>
                  ) : null}
                </section>
              ) : null}

              {collectsClinicalScope && denture ? (
                <section className="space-y-3 rounded-xl border border-slate-200 p-4">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Denture scope</p>
                    <p className="text-xs text-slate-500">{partial ? "Choose one arch or both, then mark the teeth being replaced." : "Choose one arch or both."}</p>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {["upper", "lower", "both"].map((arch) => (
                      <button key={arch} type="button" onClick={() => chooseArch(arch)} data-testid={`mv-import-arch-${arch}`} className={`rounded-lg border px-3 py-2 text-sm font-semibold capitalize ${form.arch === arch ? "border-violet-600 bg-violet-600 text-white" : "border-slate-200 text-slate-700 hover:border-violet-300"}`}>
                        {arch === "both" ? "Both arches" : `${arch} arch`}
                      </button>
                    ))}
                  </div>
                  {partial && form.arch && form.arch !== "both" ? (
                    <ToothPicker arch={form.arch} selected={form.teeth} onToggle={(tooth) => toggleTooth(tooth)} />
                  ) : null}
                  {partial && form.arch === "both" ? (
                    <div className="space-y-3" data-testid="mv-import-both-arch-pickers">
                      <ToothPicker arch="upper" selected={form.teeth} onToggle={(tooth) => toggleTooth(tooth)} />
                      <ToothPicker arch="lower" selected={form.second_teeth} onToggle={(tooth) => toggleTooth(tooth, "second_teeth")} />
                    </div>
                  ) : null}
                </section>
              ) : null}

              <Field label="Prior case note (optional)">
                <textarea rows={2} value={form.notes} onChange={(event) => update("notes", event.target.value)} placeholder="What was done before Dental OS?" data-testid="mv-import-note" className={inputClass} />
              </Field>
            </div>
          )}

          <div className="sticky -bottom-5 mt-5 flex items-center justify-end gap-2 border-t border-slate-100 bg-white py-4">
            <button type="button" onClick={onClose} disabled={submitting} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50">Cancel</button>
            <button type="submit" disabled={loading || submitting} data-testid="mv-import-submit" className="inline-flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-40">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Import ongoing case
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ToothPicker({ arch, selected, onToggle }) {
  const groups = ARCH_TEETH[arch];
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3" data-testid="mv-import-tooth-picker">
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Teeth replaced by this partial denture</p>
      <div className="grid grid-cols-[1fr_auto_1fr] items-stretch gap-2">
        <div className="grid grid-cols-4 gap-1 sm:grid-cols-8">
          {groups.left.map((tooth) => <ToothButton key={tooth} tooth={tooth} selected={selected.includes(tooth)} onToggle={onToggle} />)}
        </div>
        <div className="flex w-7 flex-col items-center justify-center" aria-label="Dental midline">
          <span className="mb-1 text-[9px] font-bold uppercase tracking-wider text-violet-500 [writing-mode:vertical-rl] rotate-180">Midline</span>
          <span className="h-full min-h-10 w-px bg-violet-400" />
        </div>
        <div className="grid grid-cols-4 gap-1 sm:grid-cols-8">
          {groups.right.map((tooth) => <ToothButton key={tooth} tooth={tooth} selected={selected.includes(tooth)} onToggle={onToggle} />)}
        </div>
      </div>
    </div>
  );
}

function ToothButton({ tooth, selected, onToggle }) {
  return (
    <button type="button" onClick={() => onToggle(tooth)} aria-pressed={selected} data-testid={`mv-import-tooth-${tooth}`} className={`min-h-9 rounded-md border text-xs font-semibold ${selected ? "border-violet-600 bg-violet-600 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-violet-300"}`}>
      {tooth}
    </button>
  );
}

function MoneyInput({ value, onChange, testId }) {
  return (
    <div className="flex overflow-hidden rounded-lg border border-slate-200 bg-white focus-within:border-violet-500 focus-within:ring-2 focus-within:ring-violet-100">
      <span className="flex items-center border-r border-slate-200 bg-slate-50 px-3 text-sm text-slate-500">₱</span>
      <input type="number" min="0" step="0.01" value={value} onChange={(event) => onChange(event.target.value)} data-testid={testId} className="min-w-0 flex-1 px-3 py-2 text-sm outline-none" />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold text-slate-700">{label}</span>
      {children}
    </label>
  );
}

function formatPeso(centavos) {
  return `₱${(Number(centavos || 0) / 100).toLocaleString("en-PH", { maximumFractionDigits: 2 })}`;
}

const inputClass = "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100";
